import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  ShoppingCart,
  RefreshCw,
  Clock,
  Mail,
  Phone,
  DollarSign,
  ExternalLink,
  Send,
  Package,
  User,
  AlertCircle,
  CheckCircle,
  XCircle,
  Search,
  Filter,
  ChevronDown,
  MessageCircle,
  Loader2,
  Activity,
  Zap
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import axios from 'axios';
import { useSettings } from '@/context/SettingsContext';
import { usePermissions } from '@/hooks/usePermissions';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AbandonedCheckoutsPage = () => {
  const { formatCurrency } = useSettings();
  const { hasPermission } = usePermissions();
  const canSync = hasPermission('abandoned_checkouts.sync');
  const canTriggerJourney = hasPermission('abandoned_checkouts.trigger_journey');
  const [checkouts, setCheckouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [timeFilter, setTimeFilter] = useState('all');
  const [selectedCheckout, setSelectedCheckout] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [triggeringJourney, setTriggeringJourney] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCheckouts, setTotalCheckouts] = useState(0);
  const [checkoutsPerPage] = useState(20);
  
  const [stats, setStats] = useState({
    total: 0,
    totalValue: 0,
    recovered: 0,
    recoveredValue: 0,
    pending: 0,
    abandoned: 0,
    triggered: 0,
    recoveryRate: 0
  });

  // Shopify sync status (confidence widget)
  const [syncStatus, setSyncStatus] = useState(null);
  const loadSyncStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${API_BASE_URL}/shopify/abandoned-checkouts/sync-status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSyncStatus(res.data);
    } catch (e) {
      // Non-fatal — widget simply hides
    }
  }, []);
  useEffect(() => {
    loadSyncStatus();
    const t = setInterval(loadSyncStatus, 60000);
    return () => clearInterval(t);
  }, [loadSyncStatus]);

  const formatRelative = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      const diff = (Date.now() - d.getTime()) / 1000;
      if (diff < 60) return `${Math.round(diff)}s ago`;
      if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
      if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
      return d.toLocaleString();
    } catch {
      return iso;
    }
  };
  const formatEta = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      const diff = (d.getTime() - Date.now()) / 1000;
      if (diff <= 0) return 'Due now';
      if (diff < 60) return `in ${Math.round(diff)}s`;
      if (diff < 3600) return `in ${Math.round(diff / 60)}m`;
      return `in ${Math.round(diff / 3600)}h`;
    } catch {
      return iso;
    }
  };

  // Load abandoned checkouts with pagination
  const loadCheckouts = useCallback(async (page = 1, showRefreshToast = false) => {
    if (showRefreshToast) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    
    try {
      const token = localStorage.getItem('token');
      
      // Build query params with pagination
      const params = new URLSearchParams();
      params.append('limit', checkoutsPerPage.toString());
      params.append('skip', ((page - 1) * checkoutsPerPage).toString());
      
      // Status filter - use our local status
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      
      // First try to get from our local pending-checkouts endpoint (has better data)
      const response = await axios.get(
        `${API_BASE_URL}/shopify/pending-checkouts?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      let fetchedCheckouts = response.data.checkouts || [];
      const total = response.data.total || 0;
      const statsData = response.data.stats || {};
      
      // Calculate hours since creation for each checkout
      fetchedCheckouts = fetchedCheckouts.map(checkout => ({
        ...checkout,
        hours_since_creation: checkout.stored_at || checkout.checkout_created_at
          ? Math.floor((new Date() - new Date(checkout.stored_at || checkout.checkout_created_at)) / (1000 * 60 * 60))
          : null,
        // Map fields for display compatibility
        created_at: checkout.checkout_created_at || checkout.stored_at,
        total_price: parseFloat(checkout.total_price) || 0,
        customer: {
          first_name: checkout.customer_name?.split(' ')[0] || '',
          last_name: checkout.customer_name?.split(' ').slice(1).join(' ') || ''
        },
        is_recovered: checkout.status === 'recovered'
      }));
      
      setCheckouts(fetchedCheckouts);
      setTotalCheckouts(total);
      setCurrentPage(page);
      
      // Calculate stats from response
      const totalValue = fetchedCheckouts.reduce((sum, c) => sum + (c.total_price || 0), 0);
      
      setStats({
        total: statsData.pending + statsData.recovered + statsData.abandoned + statsData.triggered || total,
        totalValue,
        recovered: statsData.recovered || 0,
        recoveredValue: 0, // Would need separate calculation
        pending: statsData.pending || 0,
        abandoned: statsData.abandoned || 0,
        triggered: statsData.triggered || 0,
        recoveryRate: response.data.recovery_rate || 0
      });
      
      if (showRefreshToast) {
        toast.success(`Loaded ${fetchedCheckouts.length} checkouts`);
      }
    } catch (error) {
      console.error('Error loading abandoned checkouts:', error);
      
      // Fallback to Shopify API if our endpoint fails
      try {
        const token = localStorage.getItem('token');
        const params = new URLSearchParams();
        params.append('limit', '100');
        
        const response = await axios.get(
          `${API_BASE_URL}/shopify/abandoned-checkouts?${params.toString()}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data.success) {
          let fetchedCheckouts = response.data.checkouts || [];
          fetchedCheckouts = fetchedCheckouts.map(checkout => ({
            ...checkout,
            hours_since_creation: checkout.created_at
              ? Math.floor((new Date() - new Date(checkout.created_at)) / (1000 * 60 * 60))
              : null
          }));
          
          setCheckouts(fetchedCheckouts);
          setTotalCheckouts(fetchedCheckouts.length);
          
          const total = fetchedCheckouts.length;
          const totalValue = fetchedCheckouts.reduce((sum, c) => sum + c.total_price, 0);
          const recovered = fetchedCheckouts.filter(c => c.is_recovered).length;
          
          setStats({ total, totalValue, recovered, recoveredValue: 0, pending: total - recovered, abandoned: 0, triggered: 0, recoveryRate: 0 });
        }
      } catch (fallbackError) {
        toast.error('Failed to load abandoned checkouts');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [statusFilter, checkoutsPerPage]);

  useEffect(() => {
    loadCheckouts(1);
  }, [loadCheckouts]);
  
  // Handle page change
  const handlePageChange = (page) => {
    loadCheckouts(page);
  };
  
  // Calculate total pages
  const totalPages = Math.ceil(totalCheckouts / checkoutsPerPage);

  // Trigger journey for a checkout
  const handleTriggerJourney = async (checkout) => {
    setTriggeringJourney(checkout.id);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/shopify/abandoned-checkouts/${checkout.id}/trigger-journey`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success('Recovery journey triggered successfully');
      } else {
        toast.error(response.data.message || 'Failed to trigger journey');
      }
    } catch (error) {
      console.error('Error triggering journey:', error);
      toast.error(error.response?.data?.detail || 'Failed to trigger journey');
    } finally {
      setTriggeringJourney(null);
    }
  };

  // Filter checkouts by search
  const filteredCheckouts = checkouts.filter(checkout => {
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    return (
      checkout.email?.toLowerCase().includes(query) ||
      checkout.phone?.includes(query) ||
      checkout.customer?.first_name?.toLowerCase().includes(query) ||
      checkout.customer?.last_name?.toLowerCase().includes(query) ||
      checkout.line_items?.some(item => item.title?.toLowerCase().includes(query))
    );
  });

  // Format time ago
  const formatTimeAgo = (hours) => {
    if (hours === null) return 'Unknown';
    if (hours < 1) return 'Less than 1 hour ago';
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    const days = Math.floor(hours / 24);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  };

  // Get urgency color based on hours
  const getUrgencyColor = (hours) => {
    if (hours === null) return 'text-gray-400';
    if (hours < 4) return 'text-red-400'; // Hot - act fast
    if (hours < 24) return 'text-yellow-400'; // Warm
    if (hours < 72) return 'text-blue-400'; // Cool
    return 'text-gray-400'; // Cold
  };

  const getUrgencyBadge = (hours) => {
    if (hours === null) return null;
    if (hours < 4) return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Hot</Badge>;
    if (hours < 24) return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">Warm</Badge>;
    if (hours < 72) return <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">Cool</Badge>;
    return <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30">Cold</Badge>;
  };

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-playfair text-wt-text-primary">
            Abandoned Checkouts
          </h1>
          <p className="text-wt-text-muted text-sm mt-1">
            Recover lost sales by following up with customers who didn't complete their purchase
          </p>
        </div>
        
        {canSync && (
        <Button
          onClick={async () => {
            setRefreshing(true);
            try {
              const token = localStorage.getItem('token');
              const res = await axios.post(
                `${API_BASE_URL}/shopify/abandoned-checkouts/sync-from-shopify`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
              );
              toast.success(res.data?.message || 'Synced from Shopify');
            } catch (e) {
              toast.error(e?.response?.data?.detail || 'Sync from Shopify failed');
            }
            // Reload the list regardless
            await loadCheckouts(1, false);
            await loadSyncStatus();
            setRefreshing(false);
          }}
          disabled={refreshing}
          data-testid="refresh-from-shopify-btn"
          className="bg-wt-gold hover:bg-wt-gold-hover text-black"
        >
          {refreshing ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          Refresh from Shopify
        </Button>
        )}
      </div>

      {/* Shopify Sync Status Widget */}
      {syncStatus && syncStatus.stores && syncStatus.stores.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-indigo-500/10 via-indigo-500/5 to-transparent border border-indigo-500/30 rounded-xl p-4"
          data-testid="shopify-sync-status-widget"
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center">
                <Activity className="h-5 w-5 text-indigo-300" />
              </div>
              <div>
                <p className="text-sm font-medium text-wt-text-primary flex items-center gap-2">
                  Shopify Sync Status
                  <span className="flex h-2 w-2 rounded-full bg-green-400 animate-pulse" />
                </p>
                <p className="text-xs text-wt-text-muted mt-0.5">
                  Polling every {syncStatus.poll_interval_minutes} min — matches Shopify's own Abandoned Checkouts page
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4 text-xs">
              <div>
                <p className="text-wt-text-muted">In DB</p>
                <p className="font-semibold text-wt-text-primary">
                  {(syncStatus.totals?.pending || 0) + (syncStatus.totals?.abandoned || 0) + (syncStatus.totals?.triggered || 0) + (syncStatus.totals?.recovered || 0)}
                </p>
              </div>
              <div>
                <p className="text-wt-text-muted">Pending</p>
                <p className="font-semibold text-yellow-300">{syncStatus.totals?.pending || 0}</p>
              </div>
              <div>
                <p className="text-wt-text-muted">Recovered</p>
                <p className="font-semibold text-green-300">{syncStatus.totals?.recovered || 0}</p>
              </div>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-indigo-500/20 grid grid-cols-1 md:grid-cols-2 gap-3">
            {syncStatus.stores.map((s) => (
              <div
                key={s.shop_domain}
                className="flex items-center justify-between bg-wt-bg/40 rounded-lg px-3 py-2"
                data-testid={`sync-status-store-${s.shop_domain}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-mono text-wt-text-primary truncate">
                    {s.shop_domain}
                  </p>
                  <p className="text-xs text-wt-text-muted mt-0.5">
                    Last poll: <span className="text-wt-text-secondary">{formatRelative(s.last_poll_at)}</span>
                    {' · '}
                    Next: <span className="text-indigo-300">{formatEta(s.next_poll_eta)}</span>
                  </p>
                </div>
                <div className="flex items-center gap-3 text-xs whitespace-nowrap">
                  <div className="text-right">
                    <p className="text-wt-text-muted">Last cycle</p>
                    <p className="font-semibold text-wt-text-primary">
                      +{s.last_poll_stats?.upserted ?? 0}
                      <span className="text-wt-text-muted font-normal"> / {s.last_poll_stats?.fetched ?? 0} fetched</span>
                    </p>
                  </div>
                  <Zap className="h-4 w-4 text-indigo-300" />
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-wt-paper border border-wt-border rounded-xl p-4"
        >
          <div className="flex items-center gap-2 mb-2">
            <Clock className="h-4 w-4 text-yellow-400" />
            <span className="text-xs text-wt-text-muted">Pending Check</span>
          </div>
          <p className="text-2xl font-bold text-yellow-400">{stats.pending}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-wt-paper border border-wt-border rounded-xl p-4"
        >
          <div className="flex items-center gap-2 mb-2">
            <XCircle className="h-4 w-4 text-red-400" />
            <span className="text-xs text-wt-text-muted">Not Recovered</span>
          </div>
          <p className="text-2xl font-bold text-red-400">{stats.abandoned}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-wt-paper border border-wt-border rounded-xl p-4"
        >
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="h-4 w-4 text-green-400" />
            <span className="text-xs text-wt-text-muted">Recovered</span>
          </div>
          <p className="text-2xl font-bold text-green-400">{stats.recovered}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-wt-paper border border-wt-border rounded-xl p-4"
        >
          <div className="flex items-center gap-2 mb-2">
            <Send className="h-4 w-4 text-blue-400" />
            <span className="text-xs text-wt-text-muted">Journey Triggered</span>
          </div>
          <p className="text-2xl font-bold text-blue-400">{stats.triggered}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-wt-paper border border-wt-border rounded-xl p-4"
        >
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="h-4 w-4 text-green-400" />
            <span className="text-xs text-wt-text-muted">Recovery Rate</span>
          </div>
          <p className="text-2xl font-bold text-green-400">{stats.recoveryRate}%</p>
        </motion.div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-wt-text-muted" />
          <Input
            placeholder="Search by email, phone, or product..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-wt-surface border-wt-border text-wt-text-primary"
          />
        </div>
        
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setCurrentPage(1); }}>
          <SelectTrigger className="w-[150px] bg-wt-surface border-wt-border text-wt-text-primary">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-wt-surface border-wt-border">
            <SelectItem value="all" className="text-wt-text-primary">All Status</SelectItem>
            <SelectItem value="pending" className="text-wt-text-primary">Pending</SelectItem>
            <SelectItem value="abandoned" className="text-wt-text-primary">Not Recovered</SelectItem>
            <SelectItem value="recovered" className="text-wt-text-primary">Recovered</SelectItem>
            <SelectItem value="triggered" className="text-wt-text-primary">Journey Triggered</SelectItem>
          </SelectContent>
        </Select>
        
        <Select value={timeFilter} onValueChange={setTimeFilter}>
          <SelectTrigger className="w-[150px] bg-wt-surface border-wt-border text-wt-text-primary">
            <SelectValue placeholder="Time" />
          </SelectTrigger>
          <SelectContent className="bg-wt-surface border-wt-border">
            <SelectItem value="all" className="text-wt-text-primary">All Time</SelectItem>
            <SelectItem value="24h" className="text-wt-text-primary">Last 24 Hours</SelectItem>
            <SelectItem value="7d" className="text-wt-text-primary">Last 7 Days</SelectItem>
            <SelectItem value="30d" className="text-wt-text-primary">Last 30 Days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Checkouts List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-wt-gold" />
        </div>
      ) : filteredCheckouts.length === 0 ? (
        <div className="bg-wt-paper border border-wt-border rounded-xl p-12 text-center">
          <ShoppingCart className="h-16 w-16 mx-auto mb-4 text-wt-text-muted opacity-30" />
          <h3 className="text-lg font-medium text-wt-text-primary mb-2">No Abandoned Checkouts</h3>
          <p className="text-wt-text-muted">
            {searchQuery ? 'No checkouts match your search criteria' : 'All your customers are completing their purchases!'}
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {filteredCheckouts.map((checkout) => (
            <motion.div
              key={checkout.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-wt-paper border border-wt-border rounded-xl p-4 hover:border-wt-gold/30 transition-colors"
            >
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                {/* Customer Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <User className="h-4 w-4 text-wt-text-muted" />
                    <span className="font-medium text-wt-text-primary truncate">
                      {checkout.customer_name || checkout.customer
                        ? checkout.customer_name || `${checkout.customer?.first_name || ''} ${checkout.customer?.last_name || ''}`.trim() || checkout.email
                        : checkout.email || 'Unknown Customer'}
                    </span>
                    {getUrgencyBadge(checkout.hours_since_creation)}
                    {checkout.status === 'recovered' || checkout.is_recovered ? (
                      <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Recovered
                      </Badge>
                    ) : checkout.status === 'triggered' ? (
                      <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                        <Send className="h-3 w-3 mr-1" />
                        Journey Triggered
                      </Badge>
                    ) : checkout.status === 'abandoned' ? (
                      <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                        <XCircle className="h-3 w-3 mr-1" />
                        Not Recovered
                      </Badge>
                    ) : null}
                  </div>
                  
                  <div className="flex flex-wrap items-center gap-3 text-sm text-wt-text-muted">
                    {/* Shopify Checkout ID */}
                    {checkout.id && (
                      <span className="flex items-center gap-1 font-mono text-wt-gold">
                        #{checkout.id}
                      </span>
                    )}
                    {checkout.email && (
                      <span className="flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        {checkout.email}
                      </span>
                    )}
                    {checkout.phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="h-3 w-3" />
                        {checkout.phone}
                      </span>
                    )}
                    <span className={`flex items-center gap-1 ${getUrgencyColor(checkout.hours_since_creation)}`}>
                      <Clock className="h-3 w-3" />
                      {formatTimeAgo(checkout.hours_since_creation)}
                    </span>
                  </div>
                </div>

                {/* Cart Summary */}
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="flex items-center gap-1 text-sm text-wt-text-muted">
                      <Package className="h-3 w-3" />
                      {checkout.total_items} item{checkout.total_items !== 1 ? 's' : ''}
                    </div>
                    <p className="text-lg font-bold text-wt-gold">
                      {formatCurrency(checkout.total_price)}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedCheckout(checkout);
                        setShowDetailModal(true);
                      }}
                      className="border-wt-border text-wt-text-primary"
                    >
                      View Details
                    </Button>
                    
                    {!checkout.is_recovered && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            size="sm"
                            className="bg-wt-gold hover:bg-wt-gold-hover text-black"
                            disabled={triggeringJourney === checkout.id}
                          >
                            {triggeringJourney === checkout.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <>
                                <Send className="h-4 w-4 mr-1" />
                                Recover
                              </>
                            )}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="bg-wt-surface border-wt-border">
                          {canTriggerJourney && (
                          <DropdownMenuItem
                            onClick={() => handleTriggerJourney(checkout)}
                            className="text-wt-text-primary hover:bg-wt-paper"
                          >
                            <MessageCircle className="h-4 w-4 mr-2" />
                            Trigger Recovery Journey
                          </DropdownMenuItem>
                          )}
                          {checkout.abandoned_checkout_url && (
                            <DropdownMenuItem
                              onClick={() => window.open(checkout.abandoned_checkout_url, '_blank')}
                              className="text-wt-text-primary hover:bg-wt-paper"
                            >
                              <ExternalLink className="h-4 w-4 mr-2" />
                              Open Recovery Link
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>
              </div>

              {/* Line Items Preview */}
              {checkout.line_items && checkout.line_items.length > 0 && (
                <div className="mt-3 pt-3 border-t border-wt-border">
                  <div className="flex flex-wrap gap-2">
                    {checkout.line_items.slice(0, 3).map((item, idx) => (
                      <Badge
                        key={idx}
                        variant="outline"
                        className="bg-wt-surface border-wt-border text-wt-text-secondary"
                      >
                        {item.quantity}x {item.title?.slice(0, 30)}{item.title?.length > 30 ? '...' : ''}
                      </Badge>
                    ))}
                    {checkout.line_items.length > 3 && (
                      <Badge
                        variant="outline"
                        className="bg-wt-surface border-wt-border text-wt-text-muted"
                      >
                        +{checkout.line_items.length - 3} more
                      </Badge>
                    )}
                  </div>
                </div>
              )}
            </motion.div>
          ))}
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between">
              <p className="text-sm text-wt-text-muted">
                Showing {((currentPage - 1) * checkoutsPerPage) + 1} - {Math.min(currentPage * checkoutsPerPage, totalCheckouts)} of {totalCheckouts} checkouts
              </p>
              
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="border-wt-border text-wt-text-muted"
                >
                  Previous
                </Button>
                
                {/* Page numbers */}
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? "default" : "outline"}
                        size="sm"
                        onClick={() => handlePageChange(pageNum)}
                        className={currentPage === pageNum 
                          ? "bg-wt-gold text-black" 
                          : "border-wt-border text-wt-text-muted"
                        }
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="border-wt-border text-wt-text-muted"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="bg-wt-surface border-wt-border max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary flex items-center gap-2">
              Abandoned Checkout 
              <span className="font-mono text-wt-gold">#{selectedCheckout?.id}</span>
            </DialogTitle>
            <DialogDescription className="text-wt-text-muted">
              {selectedCheckout?.checkout_created_at 
                ? `Created: ${new Date(selectedCheckout.checkout_created_at).toLocaleString()}`
                : selectedCheckout?.created_at 
                  ? `Created: ${new Date(selectedCheckout.created_at).toLocaleString()}`
                  : 'Checkout details'
              }
            </DialogDescription>
          </DialogHeader>
          
          {selectedCheckout && (
            <div className="space-y-4">
              {/* Checkout ID & Status */}
              <div className="flex items-center justify-between bg-wt-paper rounded-lg p-3 border border-wt-border">
                <div>
                  <span className="text-xs text-wt-text-muted">Shopify Checkout ID</span>
                  <p className="font-mono text-lg text-wt-gold">#{selectedCheckout.id}</p>
                </div>
                <div className="text-right">
                  <span className="text-xs text-wt-text-muted">Status</span>
                  <div className="mt-1">
                    {selectedCheckout.status === 'recovered' || selectedCheckout.is_recovered ? (
                      <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Recovered</Badge>
                    ) : selectedCheckout.status === 'triggered' ? (
                      <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">Journey Triggered</Badge>
                    ) : selectedCheckout.status === 'abandoned' ? (
                      <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Not Recovered</Badge>
                    ) : (
                      <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">Pending</Badge>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Customer Info */}
              <div className="bg-wt-paper rounded-lg p-4 border border-wt-border">
                <h4 className="text-sm font-medium text-wt-text-muted mb-2">Customer Information</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-wt-text-muted">Name</label>
                    <p className="text-wt-text-primary">
                      {selectedCheckout.customer_name || (selectedCheckout.customer
                        ? `${selectedCheckout.customer.first_name || ''} ${selectedCheckout.customer.last_name || ''}`.trim()
                        : 'Not provided')}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs text-wt-text-muted">Email</label>
                    <p className="text-wt-text-primary">{selectedCheckout.email || 'Not provided'}</p>
                  </div>
                  <div>
                    <label className="text-xs text-wt-text-muted">Phone</label>
                    <p className="text-wt-text-primary">{selectedCheckout.phone || 'Not provided'}</p>
                  </div>
                  <div>
                    <label className="text-xs text-wt-text-muted">Previous Orders</label>
                    <p className="text-wt-text-primary">{selectedCheckout.customer?.orders_count || 0}</p>
                  </div>
                </div>
              </div>

              {/* Cart Items */}
              <div className="bg-wt-paper rounded-lg p-4 border border-wt-border">
                <h4 className="text-sm font-medium text-wt-text-muted mb-2">Cart Items</h4>
                <div className="space-y-2">
                  {selectedCheckout.line_items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center py-2 border-b border-wt-border last:border-0">
                      <div>
                        <p className="text-wt-text-primary font-medium">{item.title}</p>
                        {item.variant_title && (
                          <p className="text-sm text-wt-text-muted">{item.variant_title}</p>
                        )}
                        <p className="text-xs text-wt-text-muted">SKU: {item.sku || 'N/A'}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-wt-text-primary">{item.quantity} x {formatCurrency(parseFloat(item.price))}</p>
                        <p className="text-sm text-wt-gold font-medium">
                          {formatCurrency(parseFloat(item.price) * item.quantity)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t border-wt-border flex justify-between items-center">
                  <span className="text-wt-text-muted">Total</span>
                  <span className="text-xl font-bold text-wt-gold">
                    {formatCurrency(selectedCheckout.total_price)}
                  </span>
                </div>
              </div>

              {/* Shipping Address */}
              {selectedCheckout.shipping_address && (
                <div className="bg-wt-paper rounded-lg p-4 border border-wt-border">
                  <h4 className="text-sm font-medium text-wt-text-muted mb-2">Shipping Address</h4>
                  <p className="text-wt-text-primary">{selectedCheckout.shipping_address.name}</p>
                  <p className="text-wt-text-secondary text-sm">
                    {selectedCheckout.shipping_address.address1}
                    {selectedCheckout.shipping_address.address2 && `, ${selectedCheckout.shipping_address.address2}`}
                  </p>
                  <p className="text-wt-text-secondary text-sm">
                    {selectedCheckout.shipping_address.city}, {selectedCheckout.shipping_address.province}
                  </p>
                  <p className="text-wt-text-secondary text-sm">
                    {selectedCheckout.shipping_address.country} {selectedCheckout.shipping_address.zip}
                  </p>
                </div>
              )}

              {/* Timestamps */}
              <div className="bg-wt-paper rounded-lg p-4 border border-wt-border">
                <h4 className="text-sm font-medium text-wt-text-muted mb-2">Timeline</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <label className="text-xs text-wt-text-muted">Created</label>
                    <p className="text-wt-text-primary">
                      {selectedCheckout.created_at ? new Date(selectedCheckout.created_at).toLocaleString() : 'Unknown'}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs text-wt-text-muted">Last Updated</label>
                    <p className="text-wt-text-primary">
                      {selectedCheckout.updated_at ? new Date(selectedCheckout.updated_at).toLocaleString() : 'Unknown'}
                    </p>
                  </div>
                  {selectedCheckout.completed_at && (
                    <div>
                      <label className="text-xs text-wt-text-muted">Completed</label>
                      <p className="text-green-400">
                        {new Date(selectedCheckout.completed_at).toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            {selectedCheckout?.abandoned_checkout_url && (
              <Button
                variant="outline"
                onClick={() => window.open(selectedCheckout.abandoned_checkout_url, '_blank')}
                className="border-wt-border text-wt-text-primary"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Open Recovery Link
              </Button>
            )}
            {selectedCheckout && !selectedCheckout.is_recovered && canTriggerJourney && (
              <Button
                onClick={() => {
                  handleTriggerJourney(selectedCheckout);
                  setShowDetailModal(false);
                }}
                className="bg-wt-gold hover:bg-wt-gold-hover text-black"
              >
                <Send className="h-4 w-4 mr-2" />
                Trigger Recovery Journey
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AbandonedCheckoutsPage;
